/**
 * Tracks tenant page views for platform usage analytics.
 *
 * On each route change (debounced, consecutive-duplicate pathnames deduped) it
 * posts a page-view event attributed to the current org user. Fires only when an
 * organization is selected (in-org navigation) — the org-picker and unauthenticated
 * states are not tracked.
 *
 * Render once via {@link AnalyticsTracker} inside the tenant shell.
 */

import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

import { useOrgAuth } from '../contexts/OrgAuthContext'
import { recordPageView } from '../services/trackingClient'
import { derivePageInfo } from '../utils/pageView'

/** Debounce so rapid back-to-back navigations don't spam the endpoint. */
const DEBOUNCE_MS = 300

export function usePageViewTracking(): void {
  const { session } = useOrgAuth()
  const { pathname, search } = useLocation()
  const lastPathRef = useRef<string | null>(null)

  // `ready` flips false→true exactly once (when the session + current org load)
  // and stays true across token refreshes, so it triggers the initial track
  // without disturbing the debounce on subsequent session object churn.
  const ready = Boolean(session?.current_organization?.id && session?.user?.id)

  useEffect(() => {
    if (!ready) return
    // Dedupe consecutive identical pathnames (e.g. query-only changes, rapid nav).
    if (lastPathRef.current === pathname) return
    lastPathRef.current = pathname

    const { moduleKey, pageKey } = derivePageInfo(pathname)
    const handle = setTimeout(() => {
      void recordPageView({
        module_key: moduleKey,
        page_key: pageKey,
        search: search || undefined,
      })
    }, DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [pathname, search, ready])
}
